import React from 'react';

interface HomePageProps {
  onSelectStudentCode: () => void;
  onSelectStudentDetails: () => void;
  onSelectStudentCodeSearch: () => void;
  isLoading?: boolean;
}

const HomePage: React.FC<HomePageProps> = ({
  onSelectStudentCode,
  onSelectStudentDetails,
  onSelectStudentCodeSearch,
  isLoading = false,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white p-6 shadow-lg">
        <div className="w-full px-4 md:max-w-6xl md:mx-auto flex items-center gap-4">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md">
            <span className="text-2xl font-bold text-primary-600">SDA</span>
          </div>
          <div>
            <h1 className="text-3xl font-bold">School Fee Payment Portal</h1>
            <p className="text-primary-100">SDA School, Ranchi</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            Welcome, Parents!
          </h2>
          <p className="text-xl text-gray-600">
            Select how you would like to view your student's fee details
          </p>
        </div>

        {/* Options Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-8">
          {/* Option 1: Quick Search by Student Code */}
          <div
            onClick={onSelectStudentCodeSearch}
            className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 cursor-pointer transform hover:scale-105 border-2 border-purple-200"
          >
            <div className="mb-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                <svg
                  className="w-8 h-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">
              Quick Search
            </h3>
            <p className="text-gray-600 text-center mb-4 text-sm">
              Search by class, section and student name
            </p>
            <div className="text-center">
              <button
                disabled={isLoading}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 text-sm"
              >
                {isLoading ? 'Loading...' : 'Quick Search'}
              </button>
            </div>
          </div>

          {/* Option 2: Student Code */}
          <div
            onClick={onSelectStudentCode}
            className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 cursor-pointer transform hover:scale-105"
          >
            <div className="mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H5a2 2 0 00-2 2v10a2 2 0 002 2h5m0 0V4m0 12H9m11-5a5 5 0 10-10 0v5h10v-5z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">
              I Have Student Code
            </h3>
            <p className="text-gray-600 text-center mb-4 text-sm">
              If you know your student's code, enter it here to quickly access fee details
            </p>
            <div className="text-center">
              <button
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 text-sm"
              >
                {isLoading ? 'Loading...' : 'Enter Student Code'}
              </button>
            </div>
          </div>

          {/* Option 3: Student Details */}
          <div
            onClick={onSelectStudentDetails}
            className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 cursor-pointer transform hover:scale-105"
          >
            <div className="mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">
              Search by Student Details
            </h3>
            <p className="text-gray-600 text-center mb-4 text-sm">
              Don't know your student code? Select academic year, class, and name to find fee details
            </p>
            <div className="text-center">
              <button
                disabled={isLoading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 text-sm"
              >
                {isLoading ? 'Loading...' : 'Search by Details'}
              </button>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-16 bg-blue-50 rounded-xl p-8 max-w-4xl mx-auto border-l-4 border-blue-600">
          <h4 className="text-xl font-bold text-gray-800 mb-3">Important Information</h4>
          <ul className="text-gray-700 space-y-2">
            <li className="flex items-start">
              <span className="text-blue-600 font-bold mr-3">•</span>
              <span>Your student code is a unique identifier provided by the school</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 font-bold mr-3">•</span>
              <span>All fee payments must be completed before the due date</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 font-bold mr-3">•</span>
              <span>For any queries, please contact the school office</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
